import { useEffect, useMemo, useState } from 'react'
import type { PostgrestError } from '@supabase/supabase-js'
import { ACTAS_ROSTER } from '../data/roster-config'
import { addDays, addMonths } from '../engine/civil'
import { effectiveRoster } from '../engine/effective-roster'
import type { BlockCode, DateRange, DayException, IsoDate, ShiftInstance } from '../engine/types'
import { fetchFeedItems } from '../lib/ads'
import type { FeedItem } from '../lib/ads'
import { fetchRosterData } from '../lib/roster-data'
import { assembleFeed, defaultFeedFilters } from '../lib/feed'
import type { FeedFilters as FeedFiltersState } from '../lib/feed'
import { useSession } from '../lib/session-context'
import { supabase } from '../lib/supabase'
import { FeedCard } from '../components/FeedCard'
import { FeedFilters } from '../components/FeedFilters'
import { Button, EmptyState } from '../ui'

export interface FeedScreenProps {
  /**
   * Today, injected rather than read from the clock — the same seam
   * `CalendarScreen` runs, so the screen stays deterministic in tests. The
   * client-side past-date filter is anchored here, by the plan's ruling that
   * the server's UTC `current_date` must never decide the feed's "today".
   */
  today: IsoDate
}

/** The viewer's roster, as the verdicts grade it. */
interface ViewerRoster {
  shifts: readonly ShiftInstance[]
  exceptions: readonly DayException[]
}

/**
 * The landing tab, rewritten from the Phase 1 placeholder (PLAN.md §8): the
 * honest list of what advertising actually got you. Everything about *what
 * appears* — the own-ad / same-block / past-date drops, the verdicts, the
 * band order — lives in `src/lib/feed.ts` under test; this screen owns
 * fetching, the two empty states, and liveness.
 *
 * **Data.** The feed view and the viewer's `fetchRosterData` run in
 * parallel; the roster goes through `effectiveRoster`, the one code path
 * every staff type's roster passes through, so the feed's verdicts grade an
 * FWA viewer and a block viewer identically. The window is the filter reach
 * (today → the roster's six-month forward horizon) +1 day each side, so the
 * neighbours of a boundary ad are visible to `fitAgainst`.
 *
 * **Liveness (plan R1).** Realtime rides on `advertisements`, not the view —
 * Postgres does not broadcast view changes — and every event triggers a
 * *refetch*, never a patch, so the feed is always re-derived from the
 * database and a missed event costs freshness, never correctness.
 * Window-focus refetch is the standing fallback for every case Realtime
 * drops, and both subscriptions clean up on unmount.
 *
 * **The two empty states are different facts** (`feed.ts`'s `noAds` vs
 * `hiddenCount`): nobody advertising at all vs ads that none of fit your
 * week or filters. An empty-looking feed with the wrong reason is the app
 * looking broken (§3.3), so each renders its own words.
 *
 * Defensive fallback consistent with `CalendarScreen`: post-gate the shell
 * only mounts once `decideGate` says READY, so the profile is non-null and
 * complete in practice — but a missing profile says so rather than fetching
 * with an id it does not have.
 */
export function FeedScreen({ today }: FeedScreenProps) {
  const { profile } = useSession()
  const [phase, setPhase] = useState<'loading' | 'error' | 'ready'>('loading')
  const [loadError, setLoadError] = useState<PostgrestError | null>(null)
  const [items, setItems] = useState<FeedItem[] | null>(null)
  const [viewerRoster, setViewerRoster] = useState<ViewerRoster | null>(null)
  const [filters, setFilters] = useState<FeedFiltersState>(() => defaultFeedFilters(today))
  /** Bumped by Realtime, window focus, and Retry — re-runs the load below. */
  const [reloadNonce, setReloadNonce] = useState(0)

  const userId = profile?.id ?? null
  const block = profile?.block_code ?? null

  const rosterWindow = useMemo<DateRange>(
    () => ({ from: addDays(today, -1), to: addDays(addMonths(today, 6), 1) }),
    [today],
  )

  // Realtime: refetch (never patch) on any advertisements change.
  useEffect(() => {
    const channel = supabase
      .channel('feed-advertisements')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'advertisements' }, () => {
        setReloadNonce((nonce) => nonce + 1)
      })
      .subscribe()
    return () => {
      void supabase.removeChannel(channel)
    }
  }, [])

  // The fallback ladder's bottom rung: refetch when the tab regains focus.
  useEffect(() => {
    const onFocus = () => setReloadNonce((nonce) => nonce + 1)
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  useEffect(() => {
    if (userId === null) return
    const uid: string = userId
    const viewerBlock: BlockCode | null = block
    const window = rosterWindow
    let cancelled = false
    async function load() {
      setPhase('loading')
      // The feed and the viewer's roster in parallel — the verdicts need
      // both, and neither depends on the other.
      const [feedResult, rosterResult] = await Promise.all([
        fetchFeedItems(),
        fetchRosterData(uid, window),
      ])
      if (cancelled) return
      const error = feedResult.error ?? rosterResult.error
      if (error !== null) {
        setPhase('error')
        setLoadError(error)
        return
      }
      setItems(feedResult.data)
      setViewerRoster({
        shifts: effectiveRoster(
          {
            block: viewerBlock,
            range: window,
            exceptions: rosterResult.data?.exceptions ?? [],
            explicit: rosterResult.data?.explicit ?? [],
          },
          ACTAS_ROSTER,
        ),
        exceptions: rosterResult.data?.exceptions ?? [],
      })
      setPhase('ready')
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [userId, block, rosterWindow, reloadNonce])

  const assembly = useMemo(() => {
    if (items === null || viewerRoster === null || userId === null) return null
    return assembleFeed(
      items,
      { userId, block, shifts: viewerRoster.shifts, exceptions: viewerRoster.exceptions },
      filters,
      ACTAS_ROSTER,
      today,
    )
  }, [items, viewerRoster, userId, block, filters, today])

  if (profile === null) {
    return (
      <div className="sl-stack">
        <EmptyState
          title="Your feed will fill in once your profile is set up"
          body="The feed shows shifts colleagues are advertising, filtered to the ones that fit your roster — so it needs to know your block first."
        />
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div className="sl-stack">
        <EmptyState
          title="Couldn't load the feed"
          body={
            loadError !== null
              ? `The feed couldn't be loaded — ${loadError.message}. Check your connection and try again.`
              : "The feed couldn't be loaded. Check your connection and try again."
          }
        />
        <Button onClick={() => setReloadNonce((nonce) => nonce + 1)}>Try again</Button>
      </div>
    )
  }

  if (assembly === null) {
    // Loading, named as loading — never an empty state, which would be a lie
    // about a feed nobody has fetched yet.
    return (
      <p className="sl-hint" role="status">
        Loading the feed…
      </p>
    )
  }

  return (
    <div className="sl-stack">
      <FeedFilters value={filters} onChange={setFilters} today={today} />

      {assembly.noAds ? (
        <EmptyState
          title="Nobody's advertising right now"
          body="When a colleague offers a shift for swap it appears here — filtered to the ones that actually fit your week, with the reason shown on every card."
        />
      ) : assembly.entries.length === 0 ? (
        <EmptyState
          title="No advertised shifts fit your week"
          body={
            filters.hideWarnings || filters.code !== 'ALL'
              ? `${assembly.hiddenCount} advertised shift${assembly.hiddenCount === 1 ? '' : 's'} hidden — none fit your roster or your filters. Try widening the filters above.`
              : `${assembly.hiddenCount} advertised shift${assembly.hiddenCount === 1 ? '' : 's'} hidden — none fit your roster. On this roster pattern, a D or PM has only two clean partner blocks in eight, so thin pickings are expected rather than broken.`
          }
        />
      ) : (
        <div className="ss-feed-list">
          {assembly.entries.map((entry) => (
            <FeedCard key={entry.item.advertisementId} entry={entry} />
          ))}
        </div>
      )}
    </div>
  )
}
